<template>
  <div class="lead-dashboard">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div v-if="admin_datos && admin_datos.user_type === 'lead_partner'" class="main-container">
        <div class="hero-card">
          <div>
            <p class="eyebrow">Panel Lead Partner</p>
            <h1>{{ partnerName }}</h1>
            <p class="hero-copy">Seguimiento de solicitudes y ventas atribuidas a tu canal.</p>
          </div>
          <img v-if="partnerLogo" :src="partnerLogo" alt="Logo partner" class="hero-logo" />
        </div>

        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-label">Solicitudes (30 días)</span>
            <strong>{{ summary.leads_count }}</strong>
          </div>
          <div class="summary-card">
            <span class="summary-label">Ventas (30 días)</span>
            <strong>{{ summary.sales_count }}</strong>
          </div>
          <div class="summary-card">
            <span class="summary-label">Monto pagado</span>
            <strong>{{ formatCurrency(summary.sales_amount) }}</strong>
          </div>
          <div class="summary-card">
            <span class="summary-label">Conversión</span>
            <strong>{{ formatPercent(summary.conversion_rate) }}</strong>
          </div>
          <div class="summary-card">
            <span class="summary-label">Usos de cupones (30 días)</span>
            <strong>{{ summary.coupon_usage_count }}</strong>
          </div>
          <div class="summary-card">
            <span class="summary-label">Ahorro clientes</span>
            <strong>{{ formatCurrency(summary.coupon_savings_amount) }}</strong>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <section class="panel-card coupon-panel">
          <div class="panel-header">
            <div>
              <h2>Cupones asociados</h2>
              <p>Códigos vinculados a tus reglas de atribución y ahorro confirmado en los últimos 30 días.</p>
            </div>
          </div>
          <div v-if="loading" class="empty-state">Cargando...</div>
          <div v-else-if="couponUsage.length === 0" class="empty-state">No hay cupones asociados a este partner.</div>
          <div v-else class="table-wrap">
            <table class="tablex">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Regla descuento</th>
                  <th>Estado</th>
                  <th>Usos</th>
                  <th>Ahorro clientes</th>
                  <th>Último uso</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="coupon in couponUsage" :key="coupon.code">
                  <td><span class="coupon-code">{{ coupon.code }}</span></td>
                  <td>{{ coupon.discount_rule_name || 'Sin regla de descuento' }}</td>
                  <td>
                    <span class="status-pill" :class="{ inactive: coupon.discount_rule_active === false, missing: coupon.discount_rule_active == null }">
                      {{ coupon.discount_rule_active == null ? 'Sin regla' : (coupon.discount_rule_active === false ? 'Inactiva' : 'Activa') }}
                    </span>
                  </td>
                  <td>{{ coupon.usage_count }}</td>
                  <td>{{ formatCurrency(coupon.savings_amount) }}</td>
                  <td>{{ formatDate(coupon.last_used_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <div class="content-grid">
          <section id="solicitudes-generadas" class="panel-card">
            <div class="panel-header">
              <div>
                <h2>Últimas solicitudes</h2>
                <p>Solicitudes atribuidas por UTM, backfill o código.</p>
              </div>
              <div class="pager">
                <button class="btn btn-light" @click="changePage('leads', -1)" :disabled="leadsPage <= 1 || loading">‹</button>
                <span>Pág. {{ leadsPage }}</span>
                <button class="btn btn-light" @click="changePage('leads', 1)" :disabled="loading || recentLeads.length < limit">›</button>
              </div>
            </div>
            <div v-if="loading" class="empty-state">Cargando...</div>
            <div v-else-if="recentLeads.length === 0" class="empty-state">Aún no hay solicitudes atribuidas.</div>
            <div v-else class="table-wrap">
              <table class="tablex">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Categoría</th>
                    <th>Comuna</th>
                    <th>Fuente</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="lead in recentLeads" :key="lead.id">
                    <td>{{ formatDate(lead.fecha_creado) }}</td>
                    <td>{{ lead.cliente_nombre || 'Sin nombre' }}</td>
                    <td>{{ lead.categoria || 'Sin categoría' }}</td>
                    <td>{{ lead.comuna || 'Sin comuna' }}</td>
                    <td>{{ lead.lead_partner_source || 'Sin fuente' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="panel-card">
            <div class="panel-header">
              <div>
                <h2>Últimas ventas</h2>
                <p>Órdenes pagadas asociadas a solicitudes del partner.</p>
              </div>
              <div class="pager">
                <button class="btn btn-light" @click="changePage('sales', -1)" :disabled="salesPage <= 1 || loading">‹</button>
                <span>Pág. {{ salesPage }}</span>
                <button class="btn btn-light" @click="changePage('sales', 1)" :disabled="loading || recentSales.length < limit">›</button>
              </div>
            </div>
            <div v-if="loading" class="empty-state">Cargando...</div>
            <div v-else-if="recentSales.length === 0" class="empty-state">Aún no hay ventas atribuidas.</div>
            <div v-else class="table-wrap">
              <table class="tablex">
                <thead>
                  <tr>
                    <th>Orden</th>
                    <th>Fecha pago</th>
                    <th>Cliente</th>
                    <th>Categoría</th>
                    <th>Comuna</th>
                    <th>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sale in recentSales" :key="sale.orden_id">
                    <td>#{{ sale.orden_id }}</td>
                    <td>{{ formatDate(sale.fecha_pagado) }}</td>
                    <td>{{ sale.cliente_nombre || 'Sin nombre' }}</td>
                    <td>{{ sale.categoria || 'Sin categoría' }}</td>
                    <td>{{ sale.comuna || 'Sin comuna' }}</td>
                    <td>{{ formatCurrency(sale.total_pagado) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      <div v-else class="main-container">
        <p>No tienes permisos para acceder a esta sección.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import SideNav from '@/components/SideNav.vue'
import TopNav from '@/components/TopNav.vue'

function getISODateString (date) {
  const offset = date.getTimezoneOffset() * 60000
  const adjusted = new Date(date.getTime() - offset)
  return adjusted.toISOString().split('T')[0]
}

export default {
  name: 'LeadPartnerDashboard',
  components: {
    SideNav,
    TopNav
  },
  data () {
    return {
      loading: false,
      error: '',
      recentLeads: [],
      recentSales: [],
      couponUsage: [],
      summary: {
        leads_count: 0,
        sales_count: 0,
        sales_amount: 0,
        conversion_rate: 0,
        coupon_usage_count: 0,
        coupon_savings_amount: 0
      },
      leadsPage: 1,
      salesPage: 1,
      limit: 20
    }
  },
  computed: {
    ...mapGetters(['admin_datos']),
    partner () {
      return this.admin_datos && this.admin_datos.lead_partner ? this.admin_datos.lead_partner : null
    },
    partnerName () {
      return this.partner && this.partner.nombre ? this.partner.nombre : 'Lead Partner'
    },
    partnerLogo () {
      const atributos = this.partner && this.partner.atributos ? this.partner.atributos : {}
      return atributos.logo_filename || atributos.logo_url || atributos.logo || ''
    }
  },
  created () {
    this.fetchAll()
  },
  methods: {
    defaultDateRange () {
      const today = new Date()
      const start = new Date(today)
      start.setDate(today.getDate() - 29)
      return {
        startDate: getISODateString(start),
        endDate: getISODateString(today)
      }
    },
    async fetchAll () {
      this.loading = true
      this.error = ''
      try {
        const { startDate, endDate } = this.defaultDateRange()
        const [dashboardResp, statsResp] = await Promise.all([
          axios.get(window.hostname + 'api/lead_partner/dashboard/section_paged', {
            params: {
              leads_page: this.leadsPage,
              sales_page: this.salesPage
            }
          }),
          axios.get(window.hostname + 'api/lead_partner/statistics', {
            params: {
              start_date: startDate,
              end_date: endDate
            }
          })
        ])
        this.recentLeads = dashboardResp.data.recent_leads || []
        this.recentSales = dashboardResp.data.recent_sales || []
        this.summary = statsResp.data.summary || this.summary
        this.couponUsage = statsResp.data.coupon_usage || dashboardResp.data.coupon_usage || []
      } catch (e) {
        this.error = e.response && e.response.data && (e.response.data.error || e.response.data.message)
          ? (e.response.data.error || e.response.data.message)
          : 'No se pudo cargar el panel.'
      } finally {
        this.loading = false
      }
    },
    changePage (section, delta) {
      if (section === 'leads') {
        this.leadsPage = Math.max(1, this.leadsPage + delta)
      } else {
        this.salesPage = Math.max(1, this.salesPage + delta)
      }
      this.fetchAll()
    },
    formatDate (value) {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0
      }).format(Number(value || 0))
    },
    formatPercent (value) {
      return `${Number(value || 0).toFixed(1)}%`
    }
  }
}
</script>

<style scoped>
.lead-dashboard {
  text-align: left;
}
.column-right {
  margin-left: 240px;
  padding: 88px 28px 40px;
}
.main-container {
  width: 100%;
  max-width: none;
}
.hero-card,
.panel-card,
.summary-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.06);
}
.hero-card {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
  padding: 28px;
  margin-bottom: 24px;
}
.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 8px;
}
.hero-card h1 {
  margin: 0;
  font-size: 32px;
  color: #0f172a;
}
.hero-copy {
  margin: 10px 0 0;
  color: #64748b;
}
.hero-logo {
  max-height: 64px;
  max-width: 180px;
  object-fit: contain;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}
.summary-card {
  padding: 20px;
}
.summary-label {
  display: block;
  color: #64748b;
  font-size: 13px;
  margin-bottom: 8px;
}
.summary-card strong {
  font-size: 28px;
  color: #0f172a;
}
.content-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  width: 100%;
}
.coupon-panel {
  margin-bottom: 20px;
}
.panel-card {
  padding: 20px;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}
.panel-header h2 {
  margin: 0;
  font-size: 22px;
}
.panel-header p {
  margin: 6px 0 0;
  color: #64748b;
}
.pager {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
}
.empty-state {
  padding: 28px 12px;
  text-align: center;
  color: #64748b;
}
.table-wrap {
  overflow-x: auto;
}
.tablex {
  width: 100%;
  border-collapse: collapse;
}
.tablex th,
.tablex td {
  text-align: left;
  padding: 12px 10px;
  border-bottom: 1px solid #edf2f7;
}
.tablex th {
  color: #475569;
  font-size: 13px;
  font-weight: 700;
}
.coupon-code {
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  background: #f1f5f9;
  color: #0f172a;
  font-weight: 700;
  letter-spacing: 0;
  padding: 4px 8px;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
}
.status-pill.inactive {
  background: #fee2e2;
  color: #991b1b;
}
.status-pill.missing {
  background: #f1f5f9;
  color: #475569;
}
@media (max-width: 1400px) {
  .summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 1100px) {
  .content-grid,
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 800px) {
  .column-right {
    margin-left: 0;
    padding: 88px 16px 32px;
  }
  .hero-card {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
